import React from 'react';
import MenuItemCard, { MenuItemCardSkeleton } from './MenuItemCard';
import { Search, AlertCircle } from 'lucide-react';

/**
 * MenuItemGrid - Responsive grid layout for menu items
 * 
 * Features:
 * - Responsive 2-3 column grid
 * - Skeleton loading state
 * - Empty state messaging
 * - Search results state
 */
export default function MenuItemGrid({
  items = [],
  loading = false,
  searchQuery = '',
  onAddItem,
  onOpenItemDetail,
  addingItemId = null,
  justAddedItemId = null,
  className = '',
}) {
  // Show skeletons while loading
  if (loading) {
    return (
      <div className={`grid grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6 p-4 lg:p-6 ${className}`}>
        {[...Array(6)].map((_, i) => (
          <MenuItemCardSkeleton key={i} />
        ))}
      </div>
    );
  }

  // Empty state - no items
  if (!items.length && !searchQuery) {
    return (
      <div className={`flex flex-col items-center justify-center py-20 px-8 ${className}`}>
        <div className="w-20 h-20 rounded-full bg-[#252b33] flex items-center justify-center mb-6">
          <AlertCircle size={40} className="text-gray-500" />
        </div>
        <h3 className="text-xl font-semibold text-white mb-2">
          No items available
        </h3>
        <p className="text-gray-400 text-center max-w-sm">
          This category doesn't have any items yet. Please check back later.
        </p>
      </div>
    );
  }

  // Empty state - search results
  if (!items.length && searchQuery) {
    return (
      <div className={`flex flex-col items-center justify-center py-20 px-8 ${className}`}>
        <div className="w-20 h-20 rounded-full bg-[#252b33] flex items-center justify-center mb-6">
          <Search size={40} className="text-gray-500" />
        </div>
        <h3 className="text-xl font-semibold text-white mb-2">
          No results found
        </h3>
        <p className="text-gray-400 text-center max-w-sm">
          No items match "{searchQuery}". Try a different search term.
        </p>
      </div>
    );
  }

  return (
    <div 
      className={`
        grid grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6 
        p-4 lg:p-6
        ${className}
      `}
      role="list"
      aria-label="Menu items"
    >
      {items.map((item) => (
        <MenuItemCard
          key={item.id}
          item={item}
          onAdd={onAddItem}
          onOpenDetail={onOpenItemDetail}
          isAdding={addingItemId === item.id}
          justAdded={justAddedItemId === item.id}
        />
      ))}
    </div>
  );
}
